import {EMPTY, fromEvent, Observable, of, timer} from 'rxjs';
import {map, startWith, switchMap} from 'rxjs/operators';

export interface PollWhileVisibleOptions {
  /** Interval between ticks while the tab is visible, in milliseconds. */
  intervalMs: number;
  /**
   * When `true` (default) the poll emits immediately on subscription. Set to
   * `false` when the caller already performs an initial fetch (e.g. in
   * `ngOnInit`) and only wants periodic/visibility-driven refreshes afterwards.
   */
  immediate?: boolean;
}

/**
 * Creates a polling stream that only runs while the browser tab is visible
 * (Page Visibility API). When the tab is hidden the timer is paused, and when
 * it becomes visible again the stream emits immediately and then resumes the
 * interval. This avoids background network chatter and gives the user fresh
 * data as soon as they return to the tab.
 *
 * Each emission is a monotonically increasing tick counter. Combine it with
 * `switchMap` to avoid overlapping requests and `takeUntil(destroy$)` to clean
 * up on component destruction.
 */
export function pollWhileVisible(
  options: PollWhileVisibleOptions,
): Observable<number> {
  const {intervalMs, immediate = true} = options;

  const isVisible = (): boolean =>
    typeof document === 'undefined' || document.visibilityState !== 'hidden';

  const visibility$ =
    typeof document === 'undefined'
      ? of(true)
      : fromEvent(document, 'visibilitychange').pipe(
          map(() => isVisible()),
          startWith(isVisible()),
        );

  let tick = 0;
  let firstVisibleWindow = true;

  return visibility$.pipe(
    switchMap(visible => {
      if (!visible) {
        return EMPTY;
      }
      // Only the very first visible window respects `immediate`; every
      // subsequent focus regain refreshes right away.
      const initialDelay = firstVisibleWindow && !immediate ? intervalMs : 0;
      firstVisibleWindow = false;
      return timer(initialDelay, intervalMs).pipe(map(() => tick++));
    }),
  );
}
